/**
 * TNK-155 phase E — per-lot singles aggregate (count + cost allocated).
 *
 * Used by the inventory create + bulk-add screens to show the "singles
 * already pulled" line in the lot cost-breakdown block. Previously this
 * was a `cards.filter(c => c.sourceLotId === id)` reduce over the warmed
 * AppContext list — correct when sign-in eagerly loaded the full set, but
 * silently zero once phase D made cards lazy-loaded.
 *
 * Behavior:
 *   - `lotId === null` → idle, no fetch.
 *   - Module-level cache keyed by lotId so navigating between lots in the
 *     dropdown doesn't re-fetch within a session.
 *   - Inflight dedupe so two consumers reading the same lot share one
 *     request.
 *   - Cache is freshness-naive (per page load). The screens that use it
 *     are forms — a stale "already pulled" count by a handful between
 *     opening the form and saving is acceptable.
 */
import { useEffect, useState } from "react";

import { fetchLotCardStats, type LotCardStats } from "@/context/inventorySync";

interface UseLotCardStatsResult {
  stats: LotCardStats | null;
  loading: boolean;
  error: boolean;
}

const cache = new Map<string, LotCardStats>();
const inflight = new Map<string, Promise<LotCardStats>>();

function fetchOnce(lotId: string): Promise<LotCardStats> {
  const cached = cache.get(lotId);
  if (cached) return Promise.resolve(cached);
  const existing = inflight.get(lotId);
  if (existing) return existing;
  const p = fetchLotCardStats(lotId)
    .then((stats) => {
      cache.set(lotId, stats);
      inflight.delete(lotId);
      return stats;
    })
    .catch((err) => {
      inflight.delete(lotId);
      throw err;
    });
  inflight.set(lotId, p);
  return p;
}

/** Manual cache bust — call after a mutation that changes the lot's
 *  singles roll-up (creating cards, archiving, bulk patches) so the next
 *  read goes back to the server. */
export function invalidateLotCardStats(lotId?: string): void {
  if (lotId) {
    cache.delete(lotId);
    inflight.delete(lotId);
  } else {
    cache.clear();
    inflight.clear();
  }
}

export function useLotCardStats(lotId: string | null): UseLotCardStatsResult {
  const [stats, setStats] = useState<LotCardStats | null>(() =>
    lotId ? (cache.get(lotId) ?? null) : null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!lotId) {
      setStats(null);
      setLoading(false);
      setError(false);
      return;
    }
    const cached = cache.get(lotId);
    if (cached) {
      setStats(cached);
      setLoading(false);
      setError(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    void (async () => {
      try {
        const next = await fetchOnce(lotId);
        if (!cancelled) {
          setStats(next);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lotId]);

  return { stats, loading, error };
}
